import { subDays } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import type { QueryClient } from "@tanstack/react-query";
import { lotteryResultsQuery, type DayResults, type LotteryResult } from "@/hooks/useLotteryResults";

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

export type ResultsSummary = {
  dateLabel: string; // "7 oct"
  tica: string; // "1pm 45 · 4:30pm 12" or ""
  nica: string;
};

const shortTime = (t: string) => t.replace(":00", "").replace(" ", "").toLowerCase();

const summarize = (list: LotteryResult[] | undefined) =>
  (list ?? [])
    .filter((r) => r.isPlayed && r.number && r.number !== "XX")
    .map((r) => `${shortTime(r.time)} ${r.number}`)
    .join(" · ");

/** Loads a day's results on the server (shared with the page via the query cache) and returns a short summary for titles. */
export async function loadResultsSummary(
  queryClient: QueryClient,
  daysAgo: number,
): Promise<ResultsSummary> {
  const d = subDays(toZonedTime(new Date(), "America/Costa_Rica"), daysAgo);
  const dateLabel = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  let data: DayResults | null = null;
  try {
    data = await queryClient.ensureQueryData(lotteryResultsQuery(daysAgo));
  } catch {
    data = null;
  }
  return {
    dateLabel,
    tica: summarize(data?.costaRicaResults),
    nica: summarize(data?.nicaraguaResults),
  };
}
